import {useMutation, UseMutationOptions} from '@tanstack/react-query'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {httpImpersonateExchange, HttpImpersonateExchangeOptions} from '@features/admin/http/admin.http'
import {ImpersonateExchangeResponse} from '@features/admin/types.ts'
import {errorHandler} from '@utilities/helpers'

/**
 * Exchanges the one-time verification token for the impersonated user tokens.
 *
 * The verification token is single-use and short-lived (~60s): a retry would always
 * fail with 400, hence `retry: false`.
 */
export const useImpersonateExchange = (
    options?: Omit<
        UseMutationOptions<ImpersonateExchangeResponse, unknown, HttpImpersonateExchangeOptions['payload']>,
        'mutationFn' | 'mutationKey'
    >
) => {
    return useMutation<ImpersonateExchangeResponse, unknown, HttpImpersonateExchangeOptions['payload']>({
        mutationFn: payload =>
            parseAxiosPromise({
                axiosPromise: httpImpersonateExchange({payload}),
                responseShape: ImpersonateExchangeResponse,
                onZodError: captureException
            }),
        retry: false,
        ...options,
        onError: (error, variables, context) => {
            errorHandler(error)
            options?.onError?.(error, variables, context)
        }
    })
}
